import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ringArt from '../../assets/img/portal-ring.png';
import { SignalIcon } from '../hand/icons';
import './pageSystem.css';

/**
 * The shared page system of the Host-page family — the Guest dashboard, Start something and
 * the Calendar (handoff README "Shared page system"). Hero band, stat pills, the section
 * header, ring-gradient pills, the Portal mark and the toast. Nothing here talks to the
 * network; the pages own their data and pass it in.
 */
export const RING = 'linear-gradient(90deg,#FD199F,#FF33C6,#B5B1FF,#127BFD,#22ECFB,#FAE1B4,#FCAA6D,#FD199F)';
export const ON = `linear-gradient(#000,#000) padding-box, ${RING} border-box`;

/** Kicker · H1 · sub on the left, whatever the page puts on the right (stat pills), then `children` below. */
export function PageHero({ kicker, title, sub, right, children }) {
  return (
    <section className="ps-hero">
      <div className="ps-container ps-hero-inner">
        <div className="ps-hero-top">
          <div className="ps-hero-text">
            {kicker && <span className="ps-kicker">{kicker}</span>}
            <h1 className="ps-title">{title}</h1>
            {sub && <p className="ps-sub">{sub}</p>}
          </div>
          {right}
        </div>
        {children}
      </div>
    </section>
  );
}

/** A hero stat pill: `N LABEL`. `live` adds the pulsing pink dot, `dark` the black fill, `on` the ring. */
export function StatPill({ n, label, live = false, dark = false, on = false, muted = false, onClick, ariaLabel, testId }) {
  return (
    <button
      type="button"
      className="ps-stat"
      data-live={live ? '' : undefined}
      data-dark={dark ? '' : undefined}
      data-on={on ? '' : undefined}
      data-muted={muted ? '' : undefined}
      onClick={onClick}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {live && <i aria-hidden />}
      {n !== undefined && n !== null && <b>{n}</b>}
      <span>{label}</span>
    </button>
  );
}

/** `TITLE · [pills] · ──── · detail · LINK →`. */
export function SectionHead({ title, detail, link, onLink, children, id }) {
  return (
    <div className="ps-sec">
      <h2 className="ps-h2" id={id}>{title}</h2>
      {children}
      {detail && <span className="ps-sec-detail">{detail}</span>}
      <span className="ps-rule" />
      {link && <button type="button" className="ps-sec-link" onClick={onLink}>{link}</button>}
    </div>
  );
}

/** A filter pill with an optional count. Active = black fill + the ring border. */
export function Pill({ on = false, grey = false, tall = false, count, onClick, children, role, ariaSelected, testId }) {
  return (
    <button
      type="button"
      className="ps-pill"
      data-on={on ? '' : undefined}
      data-grey={grey ? '' : undefined}
      data-h36={tall ? '' : undefined}
      onClick={onClick}
      role={role}
      aria-selected={ariaSelected}
      data-testid={testId}
    >
      {children}
      {count !== undefined && count !== null && <span>{count}</span>}
    </button>
  );
}

/** A round action button. Black by default; `grey` / `white` / `cyan` fills; `lg` = 40px. */
export function Btn({ grey = false, white = false, cyan = false, lg = false, disabled = false, onClick, children, ariaLabel, testId, style }) {
  return (
    <button
      type="button"
      className="ps-btn"
      data-grey={grey ? '' : undefined}
      data-white={white ? '' : undefined}
      data-cyan={cyan ? '' : undefined}
      data-lg={lg ? '' : undefined}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      data-testid={testId}
      style={style}
    >
      {children}
    </button>
  );
}

/**
 * The Portal mark: a circle filled with the spinning ring, a black disc inset 3px carrying the
 * broadcast glyph, and (with `ripple`) two cyan ripples. `size` is the outer box.
 */
export function RingMark({ size = 40, ripple = false, glyph, spin = 6, inset = 3 }) {
  const g = glyph ?? Math.round(size * 0.45);
  return (
    <span className="ps-mark" style={{ width: size, height: size }} aria-hidden>
      {ripple && <><em /><em /></>}
      <i style={{ backgroundImage: `url(${ringArt})`, animationDuration: `${spin}s` }} />
      <span style={{ inset }}><SignalIcon size={g} /></span>
    </span>
  );
}

/**
 * The black toast with the cyan dot, bottom-centre, gone after 2.6s. `bottom` / `bottomMobile`
 * lift it over a page's own sticky bar. Returns `[say, element]`.
 */
export function usePageToast({ bottom = 32, bottomMobile = 80 } = {}) {
  const [toast, setToast] = useState(null);
  const timer = useRef();
  useEffect(() => () => clearTimeout(timer.current), []);
  const say = useCallback((message) => {
    clearTimeout(timer.current);
    setToast(message);
    timer.current = setTimeout(() => setToast(null), 2600);
  }, []);
  const element = toast
    ? createPortal(
      <div className="pr-overlay-root ps-toast" role="status" style={{ '--ps-toast-bottom': `${bottom}px`, '--ps-toast-bottom-mobile': `${bottomMobile}px` }}>
        <i aria-hidden />
        <span>{toast}</span>
      </div>,
      document.body,
    )
    : null;
  return [say, element];
}
